import React from "react"
import { useTranslation } from "react-i18next"
import ScrollList, { type ScrollListProps } from "./scroll_list"
import { graphql, getFragment, type FragmentType } from "@/api"
import ExamplePreview from "@/common/components/example_preview"
import { BundleOutline } from "@/skylticons"

export const ExampleFragment = graphql(`
  fragment FrontpageExampleListExample_Example on Example {
    id
    signTagTag
    title
    description
    localPrice
    rasterImage
    exampleUrl
    imageAlt

    exampleable {
      __typename

      ... on Bundle {
        id
      }

      ... on Sign {
        id
      }

      ...FrontpageExampleListSignExample_Sign
      ...FrontpageExampleListBundleExample_Bundle
    }
  }
`)

export const SignExampleFragment = graphql(`
  fragment FrontpageExampleListSignExample_Sign on Sign {
    id
    previewSvg
    previewSvgB
  }
`)

export const BundleExampleFragment = graphql(`
  fragment FrontpageExampleListBundleExample_Bundle on Bundle {
    id
    itemNumber
    previewSvg
    previewSvgB

    image {
      id
      webp
      png
    }
  }
`)

interface SignExampleProps {
  exampleable: FragmentType<typeof SignExampleFragment>
}

const SignExample: React.FC<SignExampleProps> = ({ exampleable }) => {
  const sign = getFragment(SignExampleFragment, exampleable)

  return <ExamplePreview svg={sign.previewSvg} svgB={sign.previewSvgB} />
}

interface BundleExampleProps {
  exampleable: FragmentType<typeof BundleExampleFragment>
  alt?: string
}

const BundleExample: React.FC<BundleExampleProps> = ({ exampleable, alt }) => {
  const bundle = getFragment(BundleExampleFragment, exampleable)

  if (bundle.image && bundle.image.png && bundle.image.webp) {
    return (
      <picture>
        <source srcSet={bundle.image.webp} type="image/webp" />
        <img src={bundle.image.png} className="h-full w-full object-cover" alt={alt} loading="lazy" />
      </picture>
    )
  }

  if (bundle.previewSvg) {
    return <ExamplePreview svg={bundle.previewSvg} svgB={bundle.previewSvgB} />
  }

  return <BundleOutline className="w-28" />
}

interface ExampleProps {
  example: FragmentType<typeof ExampleFragment>
  businessRole: boolean
  gtmTag?: string
}

const Example: React.FC<ExampleProps> = ({ example: exampleFragment, businessRole, gtmTag }) => {
  const example = getFragment(ExampleFragment, exampleFragment)
  const exampleable = example.exampleable
  const { t } = useTranslation()

  return (
    <div>
      <a href={example.exampleUrl} className="group" data-gtm={gtmTag}>
        <div className="w-[276px] lg:w-[182px] xl:w-[192px] 2xl:w-[198px]">
          <div className="mb-2 flex h-48 justify-center rounded-xl bg-white-5 p-3">
            {exampleable.__typename === "Sign" && <SignExample exampleable={exampleable} />}
            {exampleable.__typename === "Bundle" && <BundleExample exampleable={exampleable} />}
          </div>
          <h3 className="heading-five mb-1 group-hover:underline">{example.title}</h3>
          <p className="paragraph-xs mb-2 text-ink-85">{example.description}</p>
          <div className="flex">
            <p className="heading-five mb-3 w-fit whitespace-nowrap">{example.localPrice}</p>
            <p className="mb-3 ml-1 pt-0.5 text-xs leading-normal text-ink-85">
              {t("article.price")} {businessRole ? t("order.exc_vat") : t("order.inc_vat")}
            </p>
          </div>
        </div>
      </a>
    </div>
  )
}

interface ExampleListProps extends ScrollListProps {
  examples: FragmentType<typeof ExampleFragment>[]
  businessRole: boolean
  gtmTag?: string
}

const ExampleList: React.FC<ExampleListProps> = ({ examples, businessRole, ...listProps }) => {
  return (
    <ScrollList {...listProps} buttonClassName="top-24">
      {examples.map(example => (
        <Example key={getFragment(ExampleFragment, example).id} example={example} businessRole={businessRole} />
      ))}
    </ScrollList>
  )
}

export default ExampleList
